import type { FieldExplainerContent } from './types';

export type FireExplainerContext = {
  input: {
    currentAge: number;
    retirementAge: number;
    growthRate: number;
    contributionIncrease: number;
    contributionFrequency: 'monthly' | 'yearly';
    initial401k: number;
    initialRoth: number;
    initialTaxable: number;
    contribution401k: number;
    contributionRoth: number;
    contributionTaxable: number;
    annualSpend: number;
  };
  result: {
    normalizedContribution401k: number;
    finalAge: number;
    final401k: number;
    finalRoth: number;
    finalTaxable: number;
    finalTotal: number;
    targetNumber: number;
    readinessRatio: number;
    gap: number;
  };
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const usdCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

/** Matches YEARLY_401K_CONTRIBUTION_CAP in assets/js/calculator-engines/fire-calculator-engine.js. */
const CAP_401K = 23500;

const SAFE_WITHDRAWAL_MULTIPLE = 25;

export function getFireFieldExplainers(ctx: FireExplainerContext): FieldExplainerContent[] {
  const {
    currentAge,
    retirementAge,
    growthRate,
    contributionIncrease,
    contributionFrequency,
    initial401k,
    initialRoth,
    initialTaxable,
    contribution401k,
    contributionRoth,
    contributionTaxable,
    annualSpend
  } = ctx.input;

  const { normalizedContribution401k, finalAge, final401k, finalRoth, finalTaxable, finalTotal, targetNumber, readinessRatio, gap } =
    ctx.result;

  const isMonthly = contributionFrequency === 'monthly';
  const periodWord = isMonthly ? 'month' : 'year';
  const multiplier = isMonthly ? 12 : 1;
  const years = Math.max(0, retirementAge - currentAge);
  const annualRate = growthRate / 100;
  const monthlyRate = annualRate / 12;
  const periods = isMonthly ? years * 12 : years;
  const periodRate = isMonthly ? monthlyRate : annualRate;
  const effectiveAnnual = isMonthly ? Math.pow(1 + monthlyRate, 12) - 1 : annualRate;

  /** The engine's recurrence is linear, so a starting balance's own path can be read off exactly. */
  const growAlone = (amount: number) => amount * Math.pow(1 + periodRate, periods);

  const rateLine = isMonthly
    ? `per month: balance × (1 + ${growthRate}%/12) + deposit\n12 months per year, ${years} years = ${periods} steps`
    : `per year: balance × (1 + ${growthRate}%) + deposit\n${years} years = ${periods} steps`;

  const growthFactorLine = `(1 + ${(periodRate).toFixed(6)})^${periods} = ${Math.pow(1 + periodRate, periods).toFixed(4)}×`;

  const startingBalanceEntry = (
    id: string,
    label: string,
    amount: number,
    ending: number,
    accountNote: string
  ): FieldExplainerContent => ({
    id,
    kind: 'input',
    label,
    plain: `What this account holds today. Each of the three accounts compounds on its own track, so this balance never mixes with the other two — ${accountNote}`,
    formula: `${usd.format(amount)} × ${growthFactorLine}\n  = ${usd.format(growAlone(amount))} by age ${finalAge}\n\nplus this account's deposits\n  = ${usd.format(ending)} on the chart`,
    feeds: `The ${label.replace('Initial ', '')} line on the balances chart and its column in the projection table.`
  });

  return [
    {
      id: 'currentAge',
      kind: 'input',
      label: 'Current age',
      plain:
        'Where the projection starts. The first row of the table is this age with your balances untouched — no growth and no deposits have been applied yet.',
      formula: `years projected = ${retirementAge} − ${currentAge} = ${years}\ntable rows = ${years} + 1 = ${years + 1}\n  (one opening row, then one per year)`,
      feeds: 'The age axis on the chart, the number of compounding steps, and how many escalating contribution years you get.'
    },
    {
      id: 'retirementAge',
      kind: 'input',
      label: 'Retirement age',
      plain:
        'Where the projection stops. This calculator only models the build-up. Nothing is withdrawn, sold, or taxed at this age — the last row is simply the balance you arrive with.',
      formula: `final row age = ${retirementAge}\ntotal shown there = ${usd.format(finalTotal)}`,
      feeds: 'The "Projected total" card, the target gap, and the funding ratio.',
      assumption:
        'Contributions run right up to this age and stop dead. If you plan to keep earning part-time, the real balance keeps climbing past what this shows.'
    },
    {
      id: 'growthRate',
      kind: 'input',
      label: 'Growth rate',
      plain: isMonthly
        ? `One nominal annual rate, split into twelve monthly steps of ${growthRate}%/12 because you have contributions set to monthly. Compounding twelve times makes the realised annual growth slightly higher than the number you typed.`
        : `One nominal annual rate applied once per year, in a single step, because you have contributions set to yearly. What you type is what each year multiplies by.`,
      formula: `${rateLine}\n\neffective annual growth = ${(effectiveAnnual * 100).toFixed(4)}%`,
      feeds: 'All three account lines, the total line, and every downstream card. It is the single most sensitive input here.',
      assumption:
        'The same return every year, applied identically to the 401(k), the Roth, and the taxable account. No down years, no fund fees, and no tax drag on the taxable account — which in reality would trail the two sheltered accounts.'
    },
    {
      id: 'contributionIncrease',
      kind: 'input',
      label: 'Annual contribution increase',
      plain:
        contributionIncrease > 0
          ? `Steps every deposit up by ${contributionIncrease}% each year, compounding. It applies to all three accounts at once, and year one is the unescalated amount.`
          : 'Steps every deposit up by this percentage each year. Left at zero, your deposits stay flat in nominal terms for the whole projection, which quietly shrinks them in real purchasing power.',
      formula: `year k deposit = base × (1 + ${contributionIncrease}%)^k,  k = 0 … ${Math.max(0, years - 1)}\n\nfinal-year 401(k) deposit\n  = ${usdCents.format(normalizedContribution401k)} × ${Math.pow(1 + contributionIncrease / 100, Math.max(0, years - 1)).toFixed(4)}\n  = ${usdCents.format(normalizedContribution401k * Math.pow(1 + contributionIncrease / 100, Math.max(0, years - 1)))} per ${periodWord}`,
      feeds: 'Every deposit in every year after the first, across all three accounts.',
      assumption: `The $${CAP_401K.toLocaleString('en-US')} 401(k) cap is applied to your starting contribution only. Escalation is allowed to carry later years past it, on the reasoning that the real IRS limit is indexed and rises too.`
    },
    {
      id: 'annualSpend',
      kind: 'input',
      label: 'Target annual spend',
      plain:
        'What you expect to live on per year once you stop working. It does not touch the projection at all — it exists purely to set the finish line the projection is measured against.',
      formula: `target = ${usd.format(annualSpend)} × ${SAFE_WITHDRAWAL_MULTIPLE}\n       = ${usd.format(targetNumber)}\n\n(${SAFE_WITHDRAWAL_MULTIPLE}× is the inverse of a 4% withdrawal rate)`,
      feeds: 'The "FIRE target" card, the funding ratio, and the target gap shown above the chart.',
      assumption:
        'This is a spend figure in the same nominal dollars as the projection, and it assumes no other income. Social Security, a pension, or rental income would each lower the number you actually need.'
    },
    startingBalanceEntry(
      'initial401k',
      'Initial 401(k)',
      initial401k,
      final401k,
      'the 401(k) column is only ever your 401(k) money plus its own growth.'
    ),
    {
      id: 'contribution401k',
      kind: 'input',
      label: '401(k) contribution',
      plain: `What you put into the 401(k) each ${periodWord}. This is the only contribution field with a ceiling: anything above the annual cap is trimmed before the projection runs.`,
      formula:
        contribution401k * multiplier > CAP_401K
          ? `${usdCents.format(contribution401k)} × ${multiplier} = ${usd.format(contribution401k * multiplier)} per year\nover the ${usd.format(CAP_401K)} cap, so it is trimmed:\n  ${usd.format(CAP_401K)} / ${multiplier} = ${usdCents.format(normalizedContribution401k)} per ${periodWord}`
          : `${usdCents.format(contribution401k)} × ${multiplier} = ${usd.format(contribution401k * multiplier)} per year\nunder the ${usd.format(CAP_401K)} cap, so it is used as typed`,
      feeds: 'The 401(k) line on the chart and the "401(k) normalized contribution" card.'
    },
    startingBalanceEntry(
      'initialRoth',
      'Initial Roth IRA',
      initialRoth,
      finalRoth,
      'nothing is ever swept between accounts.'
    ),
    {
      id: 'contributionRoth',
      kind: 'input',
      label: 'Roth contribution',
      plain: `What you add to the Roth IRA each ${periodWord}. Unlike the 401(k) field, no contribution limit is enforced here — the number you type is used as-is.`,
      formula: `${usdCents.format(contributionRoth)} × ${multiplier} = ${usd.format(contributionRoth * multiplier)} per year\ndeposited at the end of each ${periodWord}, then escalated by ${contributionIncrease}% a year`,
      feeds: 'The Roth IRA line on the chart and its column in the projection table.',
      assumption:
        'No IRA annual limit is enforced, and no income-based eligibility rules are checked. If you enter more than you are actually allowed to contribute, the projection will happily believe you.'
    },
    startingBalanceEntry(
      'initialTaxable',
      'Initial taxable',
      initialTaxable,
      finalTaxable,
      'this is the account you can reach before 59½, so its size matters more than its total.'
    ),
    {
      id: 'contributionTaxable',
      kind: 'input',
      label: 'Taxable contribution',
      plain: `What you invest in a regular brokerage account each ${periodWord}. It grows at exactly the same rate as the sheltered accounts here — the model does not charge it anything for being taxable.`,
      formula: `${usdCents.format(contributionTaxable)} × ${multiplier} = ${usd.format(contributionTaxable * multiplier)} per year\ncompounded at ${growthRate}%, same as every other account`,
      feeds: 'The taxable line on the chart and its column in the projection table.',
      assumption:
        'No annual tax on dividends or realised gains, and no capital-gains tax when you eventually sell. Real taxable growth runs behind a 401(k) or Roth over a long horizon.'
    },
    {
      id: 'contributionFrequency',
      kind: 'input',
      label: 'Contribution frequency',
      plain: isMonthly
        ? 'Monthly treats each contribution field as a per-month amount and runs twelve compounding steps a year, so deposits made early in a year earn returns inside that same year.'
        : 'Yearly treats each contribution field as a per-year amount and runs one compounding step a year, with the deposit landing at the very end — so a full year of contributions earns nothing until the following year.',
      formula: isMonthly
        ? `12 steps per year at ${growthRate}%/12\ndeposit ${usdCents.format(normalizedContribution401k)} / ${usdCents.format(contributionRoth)} / ${usdCents.format(contributionTaxable)} each month`
        : `1 step per year at ${growthRate}%\ndeposit ${usd.format(normalizedContribution401k)} / ${usd.format(contributionRoth)} / ${usd.format(contributionTaxable)} at year end`,
      feeds: 'Both the compounding schedule and how the 401(k) cap is converted back into a per-period figure.'
    },
    {
      id: 'projectedTotal',
      kind: 'result',
      label: 'Projected total',
      plain: `The three account balances added together in the final row, at age ${finalAge}. It is the last point on the total line and the last cell of the projection table.`,
      formula: `${usd.format(final401k)}  401(k)\n${usd.format(finalRoth)}  Roth\n${usd.format(finalTaxable)}  taxable\n  = ${usd.format(finalTotal)}`,
      feeds: 'The funding ratio, the target gap, and the annotation above the chart.',
      assumption: 'A pre-tax number. The 401(k) slice will be taxed as income on the way out, so your spendable total is smaller than this.'
    },
    {
      id: 'fireTarget',
      kind: 'result',
      label: 'FIRE target',
      plain:
        'The portfolio size the 4% rule implies for your spending. It is derived from one input only — your target annual spend — and is completely independent of your balances, ages, and return.',
      formula: `${usd.format(annualSpend)} × ${SAFE_WITHDRAWAL_MULTIPLE} = ${usd.format(targetNumber)}\n\nequivalently: ${usd.format(annualSpend)} / 0.04`,
      feeds: 'The denominator of the funding ratio and the target gap.',
      assumption:
        'The 4% rule came from historical 30-year US retirements. Retiring in your forties stretches the horizon well past what it was tested on, which is an argument for treating this as a floor.'
    },
    {
      id: 'fundingRatio',
      kind: 'result',
      label: 'Funding ratio',
      plain:
        readinessRatio >= 1
          ? 'Your projected total divided by your FIRE target. Above 100% means the projection clears the target with room to spare.'
          : 'Your projected total divided by your FIRE target. Below 100% means the projection lands short, and the gap below is what is missing.',
      formula: `${usd.format(finalTotal)} / ${usd.format(targetNumber)}\n  = ${(readinessRatio * 100).toFixed(1)}%\n\ngap = max(${usd.format(targetNumber)} − ${usd.format(finalTotal)}, 0)\n    = ${usd.format(gap)}`,
      feeds: 'The note under this card and the target gap shown above the chart.'
    },
    {
      id: 'normalizedContribution401k',
      kind: 'result',
      label: '401(k) normalized contribution',
      plain: `The 401(k) contribution the projection actually used, per ${periodWord}, after the annual cap was applied. When your input is under the cap this just echoes what you typed.`,
      formula:
        contribution401k * multiplier > CAP_401K
          ? `min(${usd.format(contribution401k * multiplier)}, ${usd.format(CAP_401K)}) / ${multiplier}\n  = ${usdCents.format(normalizedContribution401k)} per ${periodWord}\n  (trimmed from ${usdCents.format(contribution401k)})`
          : `${usd.format(contribution401k * multiplier)} per year is under ${usd.format(CAP_401K)}\n  = ${usdCents.format(normalizedContribution401k)} per ${periodWord}, unchanged`,
      feeds: 'Every 401(k) deposit in the projection, before the annual increase is layered on.',
      assumption: `A flat ${usd.format(CAP_401K)} employee cap. No employer match is modelled, and no catch-up allowance is added at 50.`
    }
  ];
}
